import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getCertifications,
  CertificationSummary,
} from 'src/api/dataVault/historyCertifications';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface ScopeEntry {
  status: Status;
  certifications: CertificationSummary[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface SliceState {
  byScope: Record<string, ScopeEntry>;
}

const STALE_MS = 5 * 60 * 1000; // 5 minutes

const initialEntry: ScopeEntry = {
  status: 'idle',
  certifications: [],
  error: null,
  lastFetchedAt: null,
};

const initialState: SliceState = { byScope: {} };

export const scopeKey = (actingOnMemberId?: number | null): string =>
  actingOnMemberId ? String(actingOnMemberId) : 'self';

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

export const fetchHistoryCertifications = createAsyncThunk<
  CertificationSummary[],
  { actingOnMemberId?: number | null; forceRefresh?: boolean },
  { state: { dataVaultHistoryCertifications: SliceState }; rejectValue: string }
>(
  'dataVaultHistoryCertifications/fetch',
  async ({ actingOnMemberId }, { rejectWithValue }) => {
    try {
      return await getCertifications({ actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load certification history'
      );
    }
  },
  {
    condition: ({ actingOnMemberId, forceRefresh }, { getState }) => {
      if (forceRefresh) {
        return true;
      }
      const key = scopeKey(actingOnMemberId);
      const entry = getState().dataVaultHistoryCertifications.byScope[key];
      if (!entry) {
        return true;
      }
      if (entry.status === 'loading') {
        return false;
      }
      if (
        entry.status === 'succeeded' &&
        entry.lastFetchedAt &&
        Date.now() - entry.lastFetchedAt < STALE_MS
      ) {
        return false;
      }
      return true;
    },
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'dataVaultHistoryCertifications',
  initialState,
  reducers: {
    invalidateScope(state, action: PayloadAction<string>) {
      delete state.byScope[action.payload];
    },
    clearAllForScope(state, action: PayloadAction<string>) {
      delete state.byScope[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHistoryCertifications.pending, (state, action) => {
        const key = scopeKey(action.meta.arg.actingOnMemberId);
        state.byScope[key] = {
          ...initialEntry,
          status: 'loading',
        };
      })
      .addCase(fetchHistoryCertifications.fulfilled, (state, action) => {
        const key = scopeKey(action.meta.arg.actingOnMemberId);
        state.byScope[key] = {
          status: 'succeeded',
          certifications: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchHistoryCertifications.rejected, (state, action) => {
        // Skip no-op from condition (aborted)
        if (action.meta.aborted) {
          return;
        }
        const key = scopeKey(action.meta.arg.actingOnMemberId);
        state.byScope[key] = {
          ...initialEntry,
          status: 'failed',
          error:
            (action.payload as string | undefined) ??
            action.error.message ??
            'Failed',
        };
      });
  },
});

export const { invalidateScope, clearAllForScope } = slice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectHistoryCertifications =
  (key: string) =>
  (state: { dataVaultHistoryCertifications: SliceState }): ScopeEntry =>
    state.dataVaultHistoryCertifications.byScope[key] ?? initialEntry;

export default slice.reducer;
